import type { MediaTransportProfile } from './delivery-assignment.js';
import { deliveryVideoCodec } from './video-codec.js';

/** H.264 rendition a browser publisher adds next to an AV1 `main` for viewers that cannot decode AV1. */
export const COMPAT_RENDITION_ID = 'compat';

/** One entry of the opt-in rendition catalogue (`GET /api/streams/:id/renditions?schema=2`). */
export interface RenditionDescriptor {
  id: string;
  /** AES-GCM rendition protocol: `main` is 1, every other rendition 2. */
  protocolVersion: 1 | 2;
  codecs: string;
  initSha256: string;
  width: number;
  height: number;
  fps: number;
  videoBitrate: number;
  audioBitrate: number;
  transportProfile?: MediaTransportProfile;
}

const ID_RE = /^[A-Za-z0-9_-]{1,32}$/;
const SHA256_RE = /^[0-9a-f]{64}$/;
const DESCRIPTOR_KEYS = new Set(['id', 'protocolVersion', 'codecs', 'initSha256', 'width', 'height', 'fps', 'videoBitrate', 'audioBitrate', 'transportProfile']);
const PROFILE_KEYS = new Set(['transportVersion', 'partDurationMs', 'gopDurationMs', 'mediaBytesPerSecond']);

const int = (v: unknown, min: number, max: number): v is number => Number.isSafeInteger(v) && (v as number) >= min && (v as number) <= max;

export function parseMediaTransportProfile(value: unknown): MediaTransportProfile | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const p = value as Record<string, unknown>;
  if (Object.keys(p).some(k => !PROFILE_KEYS.has(k))) return null;
  if (p.transportVersion !== 2 || p.partDurationMs !== 200 || p.gopDurationMs !== 1000 || !int(p.mediaBytesPerSecond, 1, 10_000_000)) return null;
  return { transportVersion: 2, partDurationMs: 200, gopDurationMs: 1000, mediaBytesPerSecond: p.mediaBytesPerSecond as number };
}

/**
 * Strict validator for a catalogue v2 entry; unknown fields reject the entry. `main` is protocolVersion 1, any other
 * rendition 2; `compat` must be H.264 and carry a v2 transport profile.
 */
export function parseRenditionDescriptorV2(value: unknown): RenditionDescriptor | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const d = value as Record<string, unknown>;
  if (Object.keys(d).some(k => !DESCRIPTOR_KEYS.has(k))) return null;
  const { id, protocolVersion, codecs, initSha256, width, height, fps, videoBitrate, audioBitrate } = d;
  if (typeof id !== 'string' || !ID_RE.test(id)) return null;
  if (protocolVersion !== (id === 'main' ? 1 : 2)) return null;
  if (typeof codecs !== 'string' || codecs.length > 256) return null;
  const codec = deliveryVideoCodec(codecs);
  if (!codec) return null;
  if (typeof initSha256 !== 'string' || !SHA256_RE.test(initSha256)) return null;
  if (!int(width, 1, 7680) || !int(height, 1, 4320) || !int(fps, 1, 120)) return null;
  if (!int(videoBitrate, 1, 100_000_000) || !int(audioBitrate, 0, 1_000_000)) return null;
  let transportProfile: MediaTransportProfile | undefined;
  if (d.transportProfile !== undefined) {
    const parsed = parseMediaTransportProfile(d.transportProfile);
    if (!parsed) return null;
    transportProfile = parsed;
  }
  if (id === COMPAT_RENDITION_ID && (codec !== 'h264' || !transportProfile)) return null;
  return {
    id, protocolVersion: protocolVersion as 1 | 2, codecs, initSha256, width, height, fps, videoBitrate, audioBitrate,
    ...(transportProfile ? { transportProfile } : {}),
  };
}
